
import { useEffect, useState } from 'react';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const services = [
  {
    title: 'Full-Service Interior Design',
    description: 'From concept to completion, we manage every detail of your project with precision and care. Our comprehensive approach includes space planning, material selection, custom furnishings, and project coordination. We work closely with architects, contractors, and artisans to bring your vision to life while ensuring the process is seamless and enjoyable.',
    image: 'https://st.hzcdn.com/fimgs/b23163cb092342b6_8573-w800-h600-b0-p0---.jpg',
    icon: 'ri-home-heart-line',
  },
  {
    title: 'Design Consulting',
    description: 'For clients who prefer a hands-on approach, our consulting services provide expert guidance and direction. We offer professional advice on layout, color palettes, furniture selection, and styling decisions. This flexible option allows you to implement changes at your own pace while benefiting from our expertise.',
    image: 'https://st.hzcdn.com/fimgs/3c4170670873f84a_0403-w800-h600-b0-p0---.jpg',
    icon: 'ri-discuss-line',
  },
  {
    title: 'Art Curation & Installation',
    description: 'Art transforms a space from decorated to complete. We source, curate, and install artwork that complements your interior while reflecting your personal taste. Our extensive network of galleries and artists allows us to find pieces that are meaningful and perfectly scaled for your space.',
    image: 'https://st.hzcdn.com/fimgs/1f213afd063e8554_7680-w800-h600-b0-p0---.jpg',
    icon: 'ri-gallery-line',
  },
  {
    title: 'Styling & Accessorizing',
    description: 'The finishing touches make all the difference. Our styling services add personality, warmth, and sophistication to your interiors through carefully selected accessories, textiles, and decorative elements. We layer textures, colors, and objects to create visual interest and depth.',
    image: 'https://st.hzcdn.com/fimgs/dba1581b04419809_0405-w800-h600-b0-p0---patio.jpg',
    icon: 'ri-layout-masonry-line',
  },
  {
    title: 'Window Treatments',
    description: 'Custom window treatments are essential for controlling light, ensuring privacy, and completing a room\'s aesthetic. We design and specify drapery, shades, and blinds tailored to your windows and interior style. From luxurious silk panels to sleek motorized shades, every detail is considered.',
    image: 'https://st.hzcdn.com/fimgs/b9113e6e0514549b_9206-w800-h600-b0-p0---.jpg',
    icon: 'ri-window-line',
  },
];

const processSteps = [
  { step: '01', title: 'Discovery', desc: 'We begin with a deep conversation about your lifestyle, goals, and aesthetic preferences.' },
  { step: '02', title: 'Concept', desc: 'A tailored design concept is developed, presenting mood boards, materials, and spatial plans.' },
  { step: '03', title: 'Execution', desc: 'We manage procurement, contractor coordination, and installation with meticulous oversight.' },
  { step: '04', title: 'Reveal', desc: 'Your space is unveiled — styled, complete, and ready to be lived in.' },
];

export default function ServicesPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar isScrolled={isScrolled} />

      {/* Hero */}
      <section className="relative h-[70vh] min-h-[500px] overflow-hidden">
        <img
          src="https://st.hzcdn.com/fimgs/e46143b906a13316_0407-w800-h600-b0-p0---.jpg"
          alt="K LINN Interiors Services"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p className="font-['Montserrat'] text-amber-400 tracking-[0.3em] text-sm uppercase mb-4">What We Offer</p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-7xl font-light text-white mb-6">
            Our Services
          </h1>
          <div className="w-16 h-px bg-amber-400 mb-6"></div>
          <p className="font-['Montserrat'] text-white/80 text-lg max-w-2xl leading-relaxed">
            Thoughtful, consultative design that begins with listening and ends with spaces that feel unmistakably yours.
          </p>
        </div>
      </section>

      {/* Intro */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light text-gray-700 leading-relaxed italic">
            "At K LINN Interiors, every project begins with a conversation. We take the time to understand how you live, what inspires you, and what you need from your space — then we build something extraordinary around that."
          </p>
          <p className="font-['Montserrat'] text-amber-600 font-medium mt-6 tracking-widest text-sm uppercase">— Karen Vanderwerp</p>
        </div>
      </section>

      {/* Services — alternating layout */}
      <section className="pb-8 px-6">
        <div className="max-w-7xl mx-auto space-y-0">
          {services.map((service, index) => (
            <div
              key={index}
              className={`flex flex-col ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'} items-stretch min-h-[480px]`}
            >
              {/* Image */}
              <div className="w-full md:w-1/2 overflow-hidden">
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top min-h-[320px] md:min-h-[480px] transition-transform duration-700 hover:scale-105"
                />
              </div>

              {/* Text */}
              <div className={`w-full md:w-1/2 flex items-center px-10 md:px-16 py-16 ${index % 2 === 0 ? 'bg-white' : 'bg-stone-50'}`}>
                <div>
                  <div className="w-10 h-16 flex items-center justify-center bg-amber-50 rounded-lg mb-6">
                    <i className={`${service.icon} text-2xl text-amber-600`}></i>
                  </div>
                  <div className="w-12 h-0.5 bg-amber-500 mb-6"></div>
                  <h2 className="font-['Cormorant_Garamond'] text-4xl font-light text-gray-900 mb-6">
                    {service.title}
                  </h2>
                  <p className="font-['Montserrat'] text-gray-600 leading-relaxed text-base">
                    {service.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="py-24 px-6 bg-gray-900">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-['Montserrat'] text-amber-400 tracking-[0.3em] text-sm uppercase mb-4">How We Work</p>
            <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-white">
              The Design Process
            </h2>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {processSteps.map((step, i) => (
              <div key={i} className="text-center group">
                <div className="font-['Cormorant_Garamond'] text-6xl font-light text-amber-500/30 group-hover:text-amber-500/60 transition-colors duration-300 mb-4">
                  {step.step}
                </div>
                <div className="w-8 h-px bg-amber-500 mx-auto mb-4"></div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl text-white mb-3">{step.title}</h3>
                <p className="font-['Montserrat'] text-gray-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Photo Strip */}
      <section className="py-0">
        <div className="flex h-64 md:h-80 overflow-hidden">
          {[
            'https://st.hzcdn.com/fimgs/1f319fb8044197b4_9586-w800-h600-b0-p0---bathroom.jpg',
            'https://st.hzcdn.com/fimgs/9e21b34c04419801_9830-w800-h600-b0-p0---pool.jpg',
            'https://st.hzcdn.com/fimgs/2b9157e3044197c4_0278-w800-h600-b0-p0---patio.jpg',
            'https://st.hzcdn.com/fimgs/7941fcaf044197d4_9672-w800-h600-b0-p0---bathroom.jpg',
          ].map((src, i) => (
            <div key={i} className="flex-1 overflow-hidden">
              <img src={src} alt="K LINN project" className="w-full h-full object-cover object-top hover:scale-110 transition-transform duration-700" />
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 bg-amber-50">
        <div className="max-w-4xl mx-auto text-center">
          <p className="font-['Montserrat'] text-amber-600 tracking-[0.3em] text-sm uppercase mb-4">Let's Connect</p>
          <h2 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-gray-900 mb-6">
            Ready to Begin Your Project?
          </h2>
          <p className="font-['Montserrat'] text-gray-600 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
            Whether you're starting from scratch or refreshing an existing space, we'd love to hear about your vision. Let's create something beautiful together.
          </p>
          <a
            href="/contact"
            className="inline-block bg-gray-900 text-white font-['Montserrat'] text-sm font-medium tracking-widest uppercase px-10 py-4 hover:bg-amber-600 transition-colors duration-300 whitespace-nowrap cursor-pointer"
          >
            Inquire Now
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
